<template>
  <AuthLayout :mode="mode">
    <Section heading="Update Password" :mode="mode" />
    <AppCard :mode="mode">
      <template #body>
        <div class="grid gap-6 sm:gap-4">
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <div class="relative">
              <AppLabel
                :mode="mode"
                htmlFor="new-password"
                title="New password"
              />
              <div class="relative flex items-center justify-between">
                <AppInput
                  :mode="mode"
                  id="new-password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="New password"
                  v-model="password"
                  @blur="checkingPasswordStrength = false"
                  @focus="checkingPasswordStrength = true"
                  :hasLabel="false"
                />
                <span
                  @click="showPassword = !showPassword"
                  class="absolute right-4 cursor-pointer"
                  :class="[
                    mode === 'dark' ? 'text-dark-text' : 'text-black-60',
                  ]"
                >
                  <Eye v-if="!showPassword" :size="20" :stroke-width="1.75" />
                  <EyeClosed v-else :size="20" :stroke-width="1.75" />
                </span>
              </div>
              <span v-if="false" class="bg-warning-100"></span>
              <div
                v-if="password && checkingPasswordStrength"
                class="absolute left-0 right-0 top-full z-10 mt-2 rounded-lg border p-4"
                :class="[
                  mode === 'dark'
                    ? 'border-dark-border bg-dark-sidebar'
                    : 'border-border-10 bg-white-100',
                ]"
              >
                <div class="mb-2">
                  <div
                    class="mb-2 h-2 w-full rounded-full"
                    :class="[
                      mode === 'dark'
                        ? 'bg-dark-border bg-opacity-50'
                        : 'bg-black-10',
                    ]"
                  >
                    <div
                      class="h-2 rounded-full transition-all duration-300 ease-in-out"
                      :class="['bg-' + strengthColor + '-100']"
                      :style="{ width: strengthPercentage + '%' }"
                    ></div>
                  </div>
                  <div class="flex items-center justify-end">
                    <span
                      class="text-[14px] font-medium"
                      :class="[
                        mode === 'dark' ? 'text-dark-text' : 'text-black-60',
                      ]"
                      >{{ strengthText }}</span
                    >
                  </div>
                </div>
                <div class="grid grid-cols-4 gap-2 sm:grid-cols-2">
                  <div
                    v-for="(requirement, index) in requirements"
                    :key="index"
                    class="flex items-center justify-center rounded-md bg-opacity-10 p-2 text-[13px] font-medium transition-colors duration-200"
                    :class="
                      requirement.met
                        ? 'bg-primary-100 text-primary-100'
                        : 'bg-danger-100 text-danger-100'
                    "
                  >
                    <span>{{ requirement.text }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <AppLabel
                :mode="mode"
                htmlFor="verify-new-password"
                title="Verify new password"
              />
              <div class="relative flex items-center justify-between">
                <AppInput
                  :mode="mode"
                  id="verify-new-password"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  placeholder="Verify new password"
                  v-model="password_confirm"
                  :hasLabel="false"
                />
                <span
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="absolute right-4 cursor-pointer"
                  :class="[
                    mode === 'dark' ? 'text-dark-text' : 'text-black-60',
                  ]"
                >
                  <Eye
                    v-if="!showConfirmPassword"
                    :size="20"
                    :stroke-width="1.75"
                  />
                  <EyeClosed v-else :size="20" :stroke-width="1.75" />
                </span>
              </div>
            </div>
            <div class="col-span-2 mt-2 sm:col-span-1 sm:mt-0">
              <AppButton
                v-if="!password || !password_confirm || strengthScore < 3"
                :isDisabled="true"
                title="Update"
              />
              <AppButton v-else title="Update" @action="updatePassword" />
            </div>
          </div>
        </div>
      </template>
    </AppCard>
  </AuthLayout>
</template>

<script>
import Section from "@C/Common/Section.vue";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import { Eye, EyeClosed } from "lucide-vue-next";
import AuthLayout from "@A/AuthLayout.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppLabel from "@C/Form/AppLabel.vue";
import AppButton from "@C/Common/AppButton.vue";

export default {
  name: "ContactSetPassword",
  components: {
    AuthLayout,
    Section,
    Eye,
    EyeClosed,
    AppCard,
    AppLabel,
    AppInput,
    AppButton,
  },
  props: {
    mode: String,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      handleError,
    };
  },
  data() {
    return {
      id: "",
      password: "",
      password_confirm: "",
      showPassword: false,
      showConfirmPassword: false,
      checkingPasswordStrength: false,
    };
  },
  created() {
    this.id = this.$route.params.id;
  },
  mounted() {
    this.getContact();
  },
  methods: {
    async getContact() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/contact/${this.id}`);
        if (data && data.contact) {
          if (!data.ok_to_update && this.isAdmin) {
            return;
          }
          if (!data.ok_to_update) {
            this.toastStore.show(
              "You do not have permission to update this contact",
              "error",
            );
            this.$router.push({ name: "settings" });
          }
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async updatePassword() {
      this.loaderStore.setIsLoading(true);
      const payload = {
        contact: {
          contact_id: this.id,
          password: this.password,
          password_confirm: this.password_confirm,
        },
      };
      try {
        const { data } = await ApiRequest().post(
          `/contact_set_password`,
          payload,
        );
        if (data) {
          this.toastStore.show("Password updated successfully!", "success");
          this.$router.push({ name: "contact-edit", params: { id: this.id } });
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
  },
  computed: {
    requirements() {
      return {
        uppercase: {
          met: /[A-Z]/.test(this.password),
          text: "Upper case",
        },
        lowercase: {
          met: /[a-z]/.test(this.password),
          text: "Lower case",
        },
        number: {
          met: /\d/.test(this.password),
          text: "Numbers",
        },
        special: {
          met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(this.password),
          text: "Symbols",
        },
      };
    },
    strengthScore() {
      if (!this.password) return 0;
      let score = 0;
      const reqs = this.requirements;
      if (this.password.length >= 10) score++;
      if (reqs.uppercase.met) score++;
      if (reqs.lowercase.met) score++;
      if (reqs.number.met) score++;
      if (reqs.special.met) score++;
      return Math.min(score, 5);
    },
    strengthLevel() {
      if (this.strengthScore < 2) return "veryWeak";
      if (this.strengthScore < 3) return "weak";
      if (this.strengthScore < 4) return "good";
      return "strong";
    },
    strengthText() {
      const texts = {
        veryWeak: "Very weak",
        weak: "Weak",
        good: "Good",
        strong: "Strong",
      };
      return texts[this.strengthLevel];
    },
    strengthPercentage() {
      return (this.strengthScore / 5) * 100;
    },
    strengthColor() {
      const colors = {
        veryWeak: "danger",
        weak: "warning",
        good: "info",
        strong: "primary",
      };
      return colors[this.strengthLevel];
    },
    isAdmin() {
      return this.userStore.user.global_admin;
    },
  },
};
</script>
